/** Lỗi đã chuẩn hóa từ tầng repository (§10.17). */
export interface ApiError {
  code: string;
  status?: number;
  message: string;
  details?: unknown;
  /** `correct_base_url` của `403 SAI_DIA_CHI_TRUY_CAP` (ADR-0097): địa chỉ chung để đưa người dùng về. */
  correctBaseUrl?: string;
}

/**
 * Một lỗi ràng buộc trong mảng `errors` của Problem Details (`ProblemFieldError`
 * của OpenAPI, ADR-0020): `pointer` trỏ vào thân yêu cầu theo tên trường JSON,
 * ví dụ `#/slug`, `#/domains/1`, `#/admin_contacts/0/email`.
 */
export interface LoiTruong {
  pointer?: string;
  parameter?: string;
  detail: string;
}

import type { KhoaDich } from '../i18n/types';

/**
 * Bản đồ mã lỗi → **khoá dịch**, không phải câu.
 *
 * Trả khoá vì hàm này là hàm thuần, gọi được từ tầng repository và từ hook —
 * những chỗ không có `useT()`. Chỗ dùng nhận khoá rồi tự dịch, nên một lỗi
 * đang hiện trên màn hình **đổi theo ngôn ngữ ngay** khi người dùng chuyển; nếu
 * hàm này trả câu tiếng Việt thì dải lỗi đó đứng nguyên một thứ tiếng cho tới
 * lần tải lại.
 *
 * `SYSTEM_INTERNAL_ERROR` là mã **mặc định** gán khi backend không trả mã nào,
 * nên cố ý **không** có ở đây — nếu khớp thì nó sẽ che mất `err.message` thật.
 *
 * Chỉ mã **chung** mới vào bảng này. Mã nghiệp vụ (`TENANT_DOMAIN_CONFLICT`,
 * `TENANT_STATUS_CONFLICT`, `INVALID_ARGUMENT`…) cố ý để ngoài: `detail` của
 * backend nói đúng slug nào trùng, tenant đang ở trạng thái nào, và cách sửa —
 * một câu chung trong catalog sẽ nuốt mất thông tin đó.
 */
const KHOA_LOI: Record<string, KhoaDich> = {
  NETWORK_ERROR: 'loi.mang',
  FORBIDDEN: 'loi.khongCoQuyen',
  NOT_FOUND: 'loi.khongTimThay',
  AUTH_INVALID_CREDENTIALS: 'loi.saiTaiKhoan',
  // Mã chung của Platform API (ADR-0020) và Gateway.
  UNAUTHENTICATED: 'loi.hetPhien',
  PERMISSION_DENIED: 'loi.khongCoQuyen',
  INTERNAL: 'loi.mayChu',
  RATE_LIMITED: 'loi.quaNhieuYeuCau',
  SERVICE_UNAVAILABLE: 'loi.tamKhongPhucVu',
  // Mã phía giao diện của `lib/api/http.ts`: máy chủ trả lời nhưng Platform API chưa chạy (ADR-0052).
  API_UNAVAILABLE: 'loi.dichVuChuaSanSang',
  IDEMPOTENCY_KEY_IN_PROGRESS: 'loi.dangXuLy',
  // Gateway chặn theo IP (ADR-0059 mục 6.1). Thường được `NguCanhGate` thay bằng màn chặn; câu này
  // chỉ hiện ở một vùng lỗi lẻ trong nhịp ngắn trước khi ngữ cảnh tải lại xong.
  IP_BLOCKED: 'loi.ipBiChan',
  IP_NOT_ALLOWED: 'loi.ipNgoaiDanhSach',
  // Cần nhập OTP vì đang ở ngoài dải tin cậy: `NguCanhGate` thay cổng bằng màn mời xác thực.
  CAN_XAC_THUC_TANG_CUONG: 'loi.canXacThucTangCuong',
  // Vào cổng qua domain thương hiệu không thuộc tổ chức của mình (ADR-0097): `NguCanhGate` thay cổng bằng màn có
  // liên kết về địa chỉ đúng; câu này chỉ hiện ở vùng lỗi lẻ trong nhịp ngắn trước đó.
  SAI_DIA_CHI_TRUY_CAP: 'loi.saiDiaChiTruyCap',
  // ADR-0058 — license-service. Ngoại lệ của luật "chỉ mã chung": `detail` của
  // các mã này không mang thêm thông tin nào cần giữ, nên câu catalog nói rõ hơn.
  LICENSE_INSTALLATION_CONFLICT: 'license.loi.trungCaiDat',
  LICENSE_APPROVAL_REQUIRED: 'license.loi.canDuyet',
  LICENSE_SELF_APPROVAL: 'license.loi.tuDuyet',
  LICENSE_SIGNING_UNAVAILABLE: 'license.loi.khongKyDuoc',
};

/**
 * Địa chỉ đúng kèm `403 SAI_DIA_CHI_TRUY_CAP`, chỉ khi là URL `http(s)` hợp lệ: giá trị đi thẳng vào `href`, không
 * nhận `javascript:` hay chuỗi lạ.
 */
export function diaChiDung(err: unknown): string | undefined {
  const url = err && typeof err === 'object' ? (err as ApiError).correctBaseUrl : undefined;
  if (!url) return undefined;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** Mã lỗi của một lỗi bất kỳ, hoặc `undefined` nếu nó không phải `ApiError`. */
export function maLoi(err: unknown): string | undefined {
  return err && typeof err === 'object' && 'code' in err ? (err as ApiError).code : undefined;
}

/**
 * Lỗi theo từng trường của một `INVALID_ARGUMENT`, khoá bằng `pointer` đã bỏ
 * tiền tố `#`: `{ '/slug': '…', '/domains/1': '…' }`. Form tra theo đường dẫn
 * của ô để hiện lỗi ngay dưới ô đó, không gom vào một dải chung.
 */
export function loiTheoTruong(err: unknown): Record<string, string> {
  if (maLoi(err) !== 'INVALID_ARGUMENT') return {};
  const ds = (err as ApiError).details;
  if (!Array.isArray(ds)) return {};
  const ra: Record<string, string> = {};
  for (const loi of ds as LoiTruong[]) {
    if (loi.pointer) ra[loi.pointer.replace(/^#/, '')] = loi.detail;
  }
  return ra;
}

const KHOA_MAC_DINH: KhoaDich = 'loi.chung';

/**
 * Kết quả giải lỗi: hoặc một khoá dịch, hoặc một câu backend đã trả sẵn.
 *
 * Hai nhánh tách nhau vì câu của backend **không dịch được** — nó tới lúc chạy,
 * không có trong catalog. Gộp hai thứ vào một `string` thì chỗ dùng không biết
 * có nên đưa qua `t()` hay không.
 */
export type KetQuaLoi =
  | { kieu: 'khoa'; khoa: KhoaDich }
  | { kieu: 'nguyenVan'; cau: string };

export function giaiLoi(err: unknown): KetQuaLoi {
  if (!err) return { kieu: 'khoa', khoa: KHOA_MAC_DINH };

  if (typeof err === 'object' && 'code' in err) {
    const apiError = err as ApiError;
    const khoa = KHOA_LOI[apiError.code];
    if (khoa) return { kieu: 'khoa', khoa };
    if (apiError.message) return { kieu: 'nguyenVan', cau: apiError.message };
  }

  if (err instanceof Error && err.message) {
    return { kieu: 'nguyenVan', cau: err.message };
  }
  return { kieu: 'khoa', khoa: KHOA_MAC_DINH };
}
